import type { Degrees, Millimeters, Point2D } from '../model/types.js';

/**
 * Definición paramétrica de un tipo de componente (plantilla).
 * Vive en la biblioteca (public/components/*.json) o embebida en el proyecto.
 * No representa una instancia colocada; solo describe cómo es el componente.
 */

export type ComponentCategory =
  | 'passive'
  | 'transistor'
  | 'ic'
  | 'diode'
  | 'led'
  | 'custom';

export type BodyShape =
  | 'cylinder-axial'
  | 'disc-radial'
  | 'cylinder-radial'
  | 'diode-axial'
  | 'to92'
  | 'to126'
  | 'to220'
  | 'dip'
  | 'led-round'
  | 'battery-9v'
  | 'push-button'
  | 'seven-segment'
  | 'custom-svg';

export interface BodyDef {
  shape: BodyShape;
  length_mm?: Millimeters;
  diameter_mm?: Millimeters;
  width_mm?: Millimeters;
  height_mm?: Millimeters;
  corner_radius_mm?: Millimeters;
  /**
   * SVG opcional para el cuerpo del componente. NUNCA para pines.
   * Los pines siempre son geometría vectorial deformable.
   */
  custom_svg?: string;
}

/** Estado inicial de un punto de doblez. */
export interface InitialBend {
  angle_in_plane_deg: number;
  angle_out_of_plane_deg: number;
}

export interface PinDef {
  /** Identificador único del pin dentro del componente (p. ej. "1", "B", "VCC"). */
  id: string;
  /** Punto de anclaje del pin al cuerpo, en coordenadas locales al componente. */
  attachment: Point2D;
  /** Ángulo inicial de salida del pin desde el anclaje (grados, en el plano). */
  direction_deg: Degrees;
  /** Longitud total original del pin, en mm. */
  length_mm: Millimeters;
  /** Diámetro del pin (ancho visual). */
  diameter_mm: Millimeters;
  /**
   * Número de puntos de doblez a lo largo del pin.
   * Se distribuyen uniformes y centrados: en posiciones
   *   k * length_mm / (bend_points_count + 1)   con k = 1..N.
   * El usuario NO puede añadir ni quitar puntos.
   */
  bend_points_count: number;
  /** Material visual del pin. */
  material?: 'copper' | 'tinned' | 'kovar';
  /**
   * Estado inicial de los dobleces. Si se omite, todos empiezan a 0.
   * Longitud esperada = bend_points_count.
   */
  initial_bends?: InitialBend[];
  /**
   * Si true, el pin no admite edición: no se muestran marcas de doblez,
   * las operaciones bend/cut/restore no tienen efecto sobre él y la
   * forma queda fija según `initial_bends`. Útil para pines rígidos
   * pre-conformados (p. ej. patillas de un DIP dobladas al montarse).
   */
  locked?: boolean;
  /**
   * Forma de la sección transversal del pin:
   *  - "round": cilíndrico (por defecto), se dibuja como línea con
   *    highlight metálico central.
   *  - "flat": sección rectangular (patilla plana tipo DIP), se dibuja
   *    como polígono trapezoidal: más ancho en el anclaje al cuerpo,
   *    más estrecho en el extremo.
   */
  shape?: 'round' | 'flat';
  /** Solo con shape="flat": ratio anchura tip / anchura base. Default 0.55. */
  tip_ratio?: number;
}

export interface AppearanceDef {
  body_color: string;
  metal_color?: string;
  material?: 'ceramic' | 'plastic' | 'metal' | 'glass';
  /** Bandas de color para resistencias (se pueden sobreescribir dinámicamente por valor). */
  resistor_bands?: string[];
  /** Etiqueta principal (part number en ICs, valor en pasivos). */
  label?: {
    text: string;
    font_size_mm: number;
  };
}

export interface ElectricalMetadata {
  default_value?: string;
  /**
   * Nombres funcionales de cada pin, alineados por índice con `pins[]`.
   * P. ej. ["B", "C", "E"] para un TO-92 o ["VCC", "GND", ...] para un IC.
   * Solo informativo por ahora; preparado para el netlist futuro.
   */
  pin_names?: string[];
}

/** Propiedad configurable por instancia (aparece en el panel de propiedades). */
export interface PropertyDef {
  key: string;
  label: string;
  type: 'enum';
  options: string[];
  default: string;
}

export interface ComponentDef {
  id: string;
  category: ComponentCategory;
  name: string;
  body: BodyDef;
  pins: PinDef[];
  appearance: AppearanceDef;
  electrical?: ElectricalMetadata;
  /** Propiedades adicionales que el usuario puede cambiar (pinout, color…). */
  properties?: PropertyDef[];
}

/** Devuelve el valor de una propiedad de instancia con fallback al default de la def. */
export function getInstanceProperty(
  instanceProps: Record<string, string> | undefined,
  def: ComponentDef,
  key: string,
): string {
  const v = instanceProps?.[key];
  if (v !== undefined) return v;
  return def.properties?.find((p) => p.key === key)?.default ?? '';
}
